import React, { useState, useEffect } from 'react';
import {BlockBase, ContentData, MediaElement} from '../../interfaces.ts';
import {getContentValue, getYouTubeEmbedUrl, isBlockTypeCorrect, resolveSrc} from '../../utils.ts';
import ShadowDomWrapper from '../../components/ShadowDomWrapper.tsx';
import UploadIcon from '../../icons/upload.tsx';
import i18n from '../../i18n/config.ts';
import { useTranslation } from 'react-i18next';

interface MediaContentData {
    src?: string;
    altText?: string;
    type?: 'image' | 'video' | 'gallery';
    gallerySrc?: string[];
}

const MediaBlock: React.FC<{
  block: BlockBase<never, MediaContentData>,
  contentData?: ContentData<MediaContentData>,
  locale: string,
  fallbackLocale: string,
  editable?: boolean,
  newMediaCallback?: (args: { blockId: string }) => Promise<string>,
  mediaLibraryCallback?: (args: { blockId: string }) => Promise<string>,
  dragAndDropMediaCallback?: (args: { blockId: string; file: File }) => Promise<string>,
  handleBlockSettingSave?: (updatedBlock: BlockBase<never, MediaContentData>) => void,
  setContentData?: (data: ContentData<MediaContentData>) => void,
  files?: MediaElement[],
  primaryColor?: string,
  availableLocales?: string[]
}> = ({ block, contentData, locale, fallbackLocale, editable = false, newMediaCallback, mediaLibraryCallback, dragAndDropMediaCallback, handleBlockSettingSave, setContentData, files, primaryColor = '#3b82f6', availableLocales = [] }) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Update i18n language when locale or fallbackLocale changes
  useEffect(() => {
    const currentLanguage = locale || fallbackLocale;
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [locale, fallbackLocale]);

  if (!isBlockTypeCorrect(block, 'mediaBlock')) {
    return null
  }

  const { id } = block;
  const { src, altText, type = 'image', gallerySrc = [] } = getContentValue(locale, fallbackLocale, block, contentData)

  // Resolve the actual src URLs
  const resolvedSrc = resolveSrc(src, files);
  const resolvedGallerySrc = gallerySrc.map(s => resolveSrc(s, files) || s);

  // Find cross-language image: when current locale has no src, check if exactly one other
  // non-fallback locale has a src set
  interface CrossLanguageImageInfo {
    locale: string;
    src: string;
  }
  const findCrossLanguageImage = (): CrossLanguageImageInfo | null => {
    if (src) return null; // Current locale already has an image
    if (availableLocales.length === 0) return null;

    const allLocales = Array.from(new Set([fallbackLocale, ...availableLocales]));
    const otherLocales = allLocales.filter(l => l !== locale);

    const localesWithImage: CrossLanguageImageInfo[] = [];
    for (const otherLocale of otherLocales) {
      // Check defaultContent first
      const defaultSrc = block.defaultContent?.[otherLocale]?.src;
      if (defaultSrc) {
        localesWithImage.push({ locale: otherLocale, src: defaultSrc });
        continue;
      }
      // Check contentData
      const contentSrc = contentData?.[otherLocale]?.[id]?.src;
      if (contentSrc) {
        localesWithImage.push({ locale: otherLocale, src: contentSrc });
      }
    }

    if (localesWithImage.length === 1) {
      return localesWithImage[0];
    }
    return null;
  };

  const crossLanguageImage = findCrossLanguageImage();

  const handleUseCrossLanguageImage = () => {
    if (!crossLanguageImage) return;
    updateContent({ src: crossLanguageImage.src });
  };

  const updateContent = (updates: Partial<MediaContentData>) => {
    if (editable && setContentData && contentData) {
      // Content editing mode - update contentData directly
      const newContentData = { ...contentData };
      if (!newContentData[locale]) {
        newContentData[locale] = {};
      }
      if (!newContentData[locale][id]) {
        newContentData[locale][id] = {};
      }
      newContentData[locale][id] = {
        ...newContentData[locale][id],
        ...updates
      };
      setContentData(newContentData);
    } else if (!editable && handleBlockSettingSave) {
      // Template editing mode - update defaultContent
      const updatedBlock = {
        ...block,
        defaultContent: {
          ...block.defaultContent,
          [locale]: {
            ...block.defaultContent?.[locale],
            ...updates
          }
        }
      };
      handleBlockSettingSave(updatedBlock);
    }
  };

  const handleNewMedia = async () => {
    if (newMediaCallback) {
      setIsUploading(true);
      try {
        const url = await newMediaCallback({ blockId: id });
        updateContent({ src: url });
      } catch (error) {
        console.error('Upload failed:', error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleMediaLibrary = async () => {
    if (mediaLibraryCallback) {
      setIsUploading(true);
      try {
        const url = await mediaLibraryCallback({ blockId: id });
        if (type === 'gallery') {
          updateContent({ gallerySrc: [...gallerySrc, url] });
        } else {
          updateContent({ src: url });
        }
      } catch (error) {
        console.error('Media library selection failed:', error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleRemoveImage = (indexToRemove: number) => {
    updateContent({ gallerySrc: gallerySrc.filter((_, i) => i !== indexToRemove) });
  };

  const handleMoveLeft = (index: number) => {
    if (index === 0) return; // Already at the start
    const newGallerySrc = [...gallerySrc];
    [newGallerySrc[index - 1], newGallerySrc[index]] = [newGallerySrc[index], newGallerySrc[index - 1]];
    updateContent({ gallerySrc: newGallerySrc });
  };

  const handleMoveRight = (index: number) => {
    if (index === gallerySrc.length - 1) return; // Already at the end
    const newGallerySrc = [...gallerySrc];
    [newGallerySrc[index], newGallerySrc[index + 1]] = [newGallerySrc[index + 1], newGallerySrc[index]];
    updateContent({ gallerySrc: newGallerySrc });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (!dragAndDropMediaCallback) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      return;
    }

    setIsUploading(true);
    try {
      if (type === 'gallery') {
        // Upload all images for gallery
        const uploadPromises = imageFiles.map(file => dragAndDropMediaCallback({ blockId: id, file }));
        const urls = await Promise.all(uploadPromises);
        updateContent({ gallerySrc: [...gallerySrc, ...urls] });
      } else {
        // Upload only the first image for single image
        const url = await dragAndDropMediaCallback({blockId: id, file: imageFiles[0] });
        updateContent({ src: url });
      }
    } catch (error) {
      console.error('Drag and drop upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  if (type === 'gallery') {
    return (
      <div data-block={id} className={id} key={id}>
        {/* Placeholder for adding new images */}
        {(newMediaCallback || mediaLibraryCallback || dragAndDropMediaCallback) && (
          <ShadowDomWrapper useTailwindStyles>
            <div
              className={`flex flex-col items-center justify-center border-dashed rounded-lg p-5 gap-3 transition-all duration-200 ease-in-out cursor-pointer ${gallerySrc.length > 0 ? 'mb-4' : ''} ${
                isDragOver
                  ? 'shadow-lg scale-[1.02] border-solid'
                  : 'hover:bg-gray-200 hover:border-gray-400'
              }`}
              style={isDragOver ? {
                height: '150px',
                '--primary-color': primaryColor,
                backgroundColor: 'color-mix(in srgb, var(--primary-color) 10%, white)',
                borderColor: 'var(--primary-color)',
                borderWidth: '1px',
                borderStyle: 'solid',
              } as React.CSSProperties : {
                height: '150px',
                backgroundColor: 'var(--placeholder-bg, #f8f8f8)',
                borderColor: 'var(--placeholder-bg, #f8f8f8)',
                borderWidth: '1px',
                borderStyle: 'solid',
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleNewMedia}
            >
              <div
                className={`transition-transform duration-200 ${isDragOver ? 'scale-110' : ''}`}
                style={isDragOver ? { '--primary-color': primaryColor, color: 'var(--primary-color)' } as React.CSSProperties : { color: 'black' }}
              >
                <UploadIcon />
              </div>
              <div
                className={`text-sm font-medium mb-2`}
                style={isDragOver ? { '--primary-color': primaryColor, color: 'color-mix(in srgb, var(--primary-color) 80%, black)' } as React.CSSProperties : { color: '#4b5563' }}
              >
                {isUploading ? t('uploading') : isDragOver ? t('dropImagesHere') : t('addImagesToSlider')}
              </div>
              {!isDragOver && (
                <div className="flex gap-2">
                  {/*{newMediaCallback && (
                    <button
                      onClick={handleNewMedia}
                      disabled={isUploading}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium shadow-sm"
                    >
                      {t('uploadImage')}
                    </button>
                  )}*/}
                  {mediaLibraryCallback && (
                    <button
                      onClick={handleMediaLibrary}
                      disabled={isUploading}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium shadow-sm"
                    >
                      {t('mediaLibrary')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </ShadowDomWrapper>
        )}
        
        {/* Gallery preview */}
        {gallerySrc.length > 0 && (
          <ShadowDomWrapper useTailwindStyles>
            <div
              className="flex gap-3 overflow-x-auto p-2 rounded"
              style={{
                backgroundColor: 'var(--placeholder-bg, #f8f8f8)',
              }}
            >
              {resolvedGallerySrc.map((imgSrc, index) => (
                <div
                  key={index}
                  className="relative flex-shrink-0 w-[150px] rounded"
                  style={{
                    borderColor: 'var(--placeholder-bg, #f8f8f8)',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                  }}
                >
                  <div className="w-[150px] h-[150px] overflow-hidden">
                    <img
                      src={imgSrc}
                      alt={`${altText} ${index + 1}`}
                      className="w-full h-full object-cover"
                      draggable={false}
                    />
                  </div>
                  <button
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full border-0 bg-red-600/80 text-white cursor-pointer text-sm flex items-center justify-center p-0 leading-none hover:bg-red-700/90"
                    title="Remove image"
                  >
                    ×
                  </button>
                  <div className="flex gap-1 p-1 bg-white/90">
                    <button
                      onClick={() => handleMoveLeft(index)}
                      disabled={index === 0}
                      className="flex-1 px-2 py-1 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
                      title="Move left"
                    >
                      ← Left
                    </button>
                    <button
                      onClick={() => handleMoveRight(index)}
                      disabled={index === gallerySrc.length - 1}
                      className="flex-1 px-2 py-1 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
                      title="Move right"
                    >
                      Right →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </ShadowDomWrapper>
        )}
      </div>
    );
  }

  // Video with URL
  if (type === 'video' && resolvedSrc) {
    const videoSrc = getYouTubeEmbedUrl(resolvedSrc, true)
    if (null === videoSrc) {
      return null
    }

    return (
      <div data-block={id} className={id} key={id}>
        <iframe
          typeof="text/html"
          width="100%"
          height="400px"
          src={videoSrc}
          style={{pointerEvents: 'none', border: 'none'}}
        />
        {(newMediaCallback || mediaLibraryCallback) && (
          <ShadowDomWrapper useTailwindStyles>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('youtubeUrl')}
              </label>
              <input
                type="text"
                value={resolvedSrc}
                onChange={(e) => updateContent({ src: e.target.value })}
                placeholder={t('youtubeUrlPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--primary-color': primaryColor, '--tw-ring-color': 'var(--primary-color)' } as React.CSSProperties}
              />
            </div>
          </ShadowDomWrapper>
        )}
      </div>
    );
  }

  // Video placeholder
  if (type === 'video' && !resolvedSrc && (newMediaCallback || mediaLibraryCallback)) {
    return (
      <ShadowDomWrapper useTailwindStyles>
        <div
          data-block={id}
          className={`${id} flex flex-col items-center justify-center border-dashed rounded-lg p-5 gap-3`}
          style={{
            height: '390px',
            backgroundColor: 'var(--placeholder-bg, #f8f8f8)',
            borderColor: 'var(--placeholder-bg, #f8f8f8)',
            borderWidth: '1px',
            borderStyle: 'solid',
          }}
          key={id}
        >
          <div className="text-sm font-medium text-gray-600 mb-2">
            {t('addYoutubeVideo')}
          </div>
          <div className="w-full max-w-md">
            <input
              type="text"
              value={src || ''}
              onChange={(e) => updateContent({ src: e.target.value })}
              placeholder={t('youtubeUrlPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ '--primary-color': primaryColor, '--tw-ring-color': 'var(--primary-color)' } as React.CSSProperties}
            />
          </div>
        </div>
      </ShadowDomWrapper>
    );
  }

  // Show placeholder if no src and we have callbacks
  if (type === 'image' && !resolvedSrc && (newMediaCallback || mediaLibraryCallback || dragAndDropMediaCallback)) {
    return (
      <ShadowDomWrapper useTailwindStyles>
        <div
          data-block={id}
          className={`${id} flex flex-col items-center justify-center border-dashed rounded-lg p-5 gap-3 transition-all duration-200 ease-in-out cursor-pointer ${
            isDragOver
              ? 'shadow-lg border-solid'
              : 'hover:bg-gray-200 hover:border-gray-400'
          }`}
          style={isDragOver ? {
            height: '390px',
            '--primary-color': primaryColor,
            backgroundColor: 'color-mix(in srgb, var(--primary-color) 10%, white)',
            borderColor: 'var(--primary-color)',
            borderWidth: '1px',
            borderStyle: 'solid',
          } as React.CSSProperties : {
            height: '390px',
            backgroundColor: 'var(--placeholder-bg, #f8f8f8)',
            borderColor: 'var(--placeholder-bg, #f8f8f8)',
            borderWidth: '1px',
            borderStyle: 'solid',
          }}
          key={id}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleNewMedia}
        >
          <div
            className={`transition-transform duration-200 ${isDragOver ? 'scale-110' : ''}`}
            style={isDragOver ? { '--primary-color': primaryColor, color: 'var(--primary-color)' } as React.CSSProperties : { color: 'black' }}
          >
            <UploadIcon />
          </div>
          <style>
            {`
              .drop-zone-description span {
                color: ${primaryColor};
              }
            `}
          </style>
          <div
            className="text-sm font-medium mb-2 drop-zone-description"
            style={isDragOver ? { '--primary-color': primaryColor, color: 'color-mix(in srgb, var(--primary-color) 80%, black)' } as React.CSSProperties : { color: 'black' }}
            dangerouslySetInnerHTML={{
              __html: isUploading ? t('uploading') : isDragOver ? t('dropImageHere') : t('dropZoneDescription')
            }}
          />
          {!isDragOver && (
            <div className="flex gap-2">
              {/*{newMediaCallback && (
                <button
                  onClick={handleNewMedia}
                  disabled={isUploading}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium shadow-sm"
                >
                  {t('uploadImage')}
                </button>
              )}*/}
              {mediaLibraryCallback && (
                <button
                  onClick={handleMediaLibrary}
                  disabled={isUploading}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium shadow-sm"
                >
                  {t('mediaLibrary')}
                </button>
              )}
            </div>
          )}
          {!isDragOver && crossLanguageImage && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleUseCrossLanguageImage();
              }}
              className="text-sm font-medium"
              style={{ '--primary-color': primaryColor, color: `var(--primary-color, ${primaryColor})`, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 } as React.CSSProperties}
            >
              {t('useImageFromLocale', { locale: crossLanguageImage.locale.toUpperCase() })}
            </button>
          )}
        </div>
      </ShadowDomWrapper>
    );
  }

  // Show image with drag and drop support if callbacks are available
  if (type === 'image' && resolvedSrc && dragAndDropMediaCallback) {
    return (
      <ShadowDomWrapper useTailwindStyles>
        <div
          data-block={id}
          className="relative inline-block w-full"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <img
            className={id}
            key={id}
            alt={altText}
            src={resolvedSrc}
            style={{ cursor: newMediaCallback ? 'pointer' : 'default' }}
            onClick={newMediaCallback ? handleNewMedia : undefined}
          />
          {isDragOver && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center border-4 border-dashed rounded-lg transition-all duration-200"
              style={{
                '--primary-color': primaryColor,
                backgroundColor: 'color-mix(in srgb, var(--primary-color) 90%, transparent)',
                borderColor: 'color-mix(in srgb, var(--primary-color) 80%, black)',
              } as React.CSSProperties}
            >
              <div className="mb-3 text-white animate-bounce">
                <UploadIcon />
              </div>
              <div className="text-lg font-bold text-white">
                {isUploading ? t('uploading') : t('dropImageToReplace')}
              </div>
            </div>
          )}
        </div>
      </ShadowDomWrapper>
    );
  }

  return <img
    data-block={id}
    className={id}
    key={id}
    alt={altText}
    src={resolvedSrc}
    style={{ cursor: newMediaCallback ? 'pointer' : 'default' }}
    onClick={newMediaCallback ? handleNewMedia : undefined}
  />;
};

export default MediaBlock;